import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Input, Select, Table, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { AppContext } from "../../AppContext";
import { memberState } from "../../state/atoms";
import DataKitApi from "../../api/DataKitApi";
import { VenueItemDto } from "../../domain/DataKitDto";
import { PermissionsDto } from "../../generated/dashboard-client";
import { hasDataKitBeta } from "../account/page/data-kit/dataKitAccess";
import { env } from "../../env/env";
import "./DataKitMenuDataPage.scss";

const ALL_KITS = "__all__";
const ALL_GROUPS = "__all__";

const KIT_PALETTE = [
  { bg: "#fff0f1", fg: "#e42436" },
  { bg: "#f9f0ff", fg: "#531dab" },
  { bg: "#f0f5ff", fg: "#1d39c4" },
  { bg: "#e6fffb", fg: "#08979c" },
  { bg: "#fff7e6", fg: "#d46b08" },
  { bg: "#f6ffed", fg: "#389e0d" },
];

function kitColors(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return KIT_PALETTE[Math.abs(hash) % KIT_PALETTE.length];
}

function groupLabel(item: VenueItemDto): string {
  const entries = item.groups || [];
  if (entries.length === 0) return item.groupName || "";
  return entries
    .map((e) => ((e.groupPath || []).length > 0 ? (e.groupPath || []).join(" / ") : e.groupName))
    .join(", ");
}

export function DataKitMenuDataPage() {
  const { location } = useContext(AppContext);
  const member = useRecoilValue(memberState);
  const unitId = location.internalId != null ? String(location.internalId) : "";

  // Owner/Admin always; everyone else needs the per-location editMenuData grant. Same
  // member.permissions[locationId] shape RegisterPage already reads. The cast is only because the
  // generated client predates the flag - dashboard/api sends it on PermissionsDto today.
  const canEditPrices = useMemo(() => {
    if (!member) return false;
    if (member.owner || member.role === "Admin") return true;
    const perm = member.permissions?.[unitId] as (PermissionsDto & { editMenuData?: boolean }) | undefined;
    return !!perm?.editMenuData;
  }, [member, unitId]);

  const [items, setItems] = useState<VenueItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [kitFilter, setKitFilter] = useState<string>(ALL_KITS);
  const [groupFilter, setGroupFilter] = useState<string>(ALL_GROUPS);
  const [editing, setEditing] = useState<{ externalId: string; size: string } | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!unitId) return;
    setLoading(true);
    DataKitApi.listVenueItems("LOCATION", unitId)
      .then((res) => setItems(res.data || []))
      .catch(() => message.error("Could not load this location's menu data"))
      .finally(() => setLoading(false));
  }, [unitId]);

  useEffect(() => {
    load();
  }, [load]);

  const kitCounts = useMemo(() => {
    const map = new Map<string, number>();
    items.forEach((i) => map.set(i.kitName, (map.get(i.kitName) || 0) + 1));
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [items]);

  const groupNames = useMemo(() => {
    const set = new Set<string>();
    items.forEach((i) => (i.groups || []).forEach((e) => set.add(e.groupName)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (kitFilter !== ALL_KITS && item.kitName !== kitFilter) return false;
      if (
        groupFilter !== ALL_GROUPS &&
        !(item.groups || []).some((e) => e.groupName === groupFilter)
      ) {
        return false;
      }
      if (!q) return true;
      return (
        item.name.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        (item.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [items, query, kitFilter, groupFilter]);

  const commitPrice = (item: VenueItemDto, size: string) => {
    if (!unitId) return;
    setSaving(true);
    DataKitApi.setVenuePriceOverride("LOCATION", unitId, item.externalId, {
      size,
      price: draft.trim() || null,
    })
      .then((res) => {
        const updated = res.data;
        setItems((current) =>
          current.map((i) => (i.externalId === updated.externalId ? updated : i))
        );
        setEditing(null);
      })
      .catch(() => message.error("Could not save that price"))
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      sorter: (a: VenueItemDto, b: VenueItemDto) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
      render: (_: string, item: VenueItemDto) => (
        <div className="menu-data-name-cell">
          <div className="menu-data-name">{item.name}</div>
          {item.description && <div className="menu-data-description">{item.description}</div>}
        </div>
      ),
    },
    {
      title: "Library",
      key: "kit",
      width: 200,
      render: (_: unknown, item: VenueItemDto) => {
        const { bg, fg } = kitColors(item.kitName);
        return (
          <span className="menu-data-kit-pill" style={{ background: bg, color: fg }}>
            {item.kitName}
          </span>
        );
      },
    },
    {
      title: "Group",
      key: "group",
      width: 220,
      render: (_: unknown, item: VenueItemDto) => groupLabel(item) || "--",
    },
    {
      title: "Price",
      key: "price",
      width: 260,
      render: (_: unknown, item: VenueItemDto) => {
        const prices = item.prices || [];
        if (prices.length === 0) return <span className="menu-data-no-price">--</span>;
        return (
          <div className="menu-data-prices">
            {prices.map((p) => {
              const isEditing =
                editing?.externalId === item.externalId && editing?.size === p.size;
              if (isEditing) {
                return (
                  <Input
                    key={p.size}
                    className="menu-data-price-input"
                    size="small"
                    autoFocus
                    value={draft}
                    disabled={saving}
                    placeholder="blank = library price"
                    onChange={(e) => setDraft(e.target.value)}
                    onPressEnter={() => commitPrice(item, p.size)}
                    onBlur={() => commitPrice(item, p.size)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setEditing(null);
                    }}
                  />
                );
              }
              return (
                <span
                  key={p.size}
                  className={`menu-data-price${canEditPrices ? " editable" : ""}`}
                  title={
                    canEditPrices
                      ? p.size
                        ? `Edit this location's price for ${p.size}`
                        : "Edit this location's price"
                      : undefined
                  }
                  onClick={() => {
                    if (!canEditPrices) return;
                    setDraft(p.overridden ? p.price : "");
                    setEditing({ externalId: item.externalId, size: p.size });
                  }}
                >
                  {p.size && <span className="menu-data-price-size">{p.size}</span>}
                  <span className="menu-data-price-value">{p.price}</span>
                  {p.overridden && <span className="menu-data-local-price">local price</span>}
                </span>
              );
            })}
          </div>
        );
      },
    },
  ];

  if (!hasDataKitBeta(member)) return null;

  return (
    <div className="menu-data-page">
      <header className="menu-data-header">
        <div className="menu-data-header-text">
          <h1>Menu Data</h1>
          <p className="menu-data-subtitle">
            All menu items available to this location. Item libraries are managed in{" "}
            <Link to={`${env.dashboardPath}/account/data-kits`}>Account Settings</Link>.
          </p>
        </div>
      </header>

      <div className="menu-data-kit-pills">
        <button
          type="button"
          className={`menu-data-kit-filter${kitFilter === ALL_KITS ? " selected" : ""}`}
          onClick={() => setKitFilter(ALL_KITS)}
        >
          All <span className="menu-data-kit-count">{items.length}</span>
        </button>
        {kitCounts.map(([name, count]) => (
          <button
            key={name}
            type="button"
            className={`menu-data-kit-filter${kitFilter === name ? " selected" : ""}`}
            onClick={() => setKitFilter(name)}
          >
            {name} <span className="menu-data-kit-count">{count}</span>
          </button>
        ))}
      </div>

      <div className="menu-data-toolbar">
        <Input
          className="menu-data-search"
          size="large"
          placeholder={`Search ${items.length} item${items.length === 1 ? "" : "s"}`}
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          allowClear
        />
        <Select
          className="menu-data-group-filter"
          size="large"
          value={groupFilter}
          onChange={setGroupFilter}
          options={[
            { label: "All groups", value: ALL_GROUPS },
            ...groupNames.map((g) => ({ label: g, value: g })),
          ]}
        />
      </div>

      {canEditPrices && (
        <p className="menu-data-edit-hint">
          Click a price to set this location&apos;s own. Leave it blank to go back to the library price.
        </p>
      )}

      <Table
        className="menu-data-table"
        dataSource={filtered}
        columns={columns}
        rowKey="externalId"
        loading={loading}
        pagination={false}
        locale={{
          emptyText: items.length === 0 ? "No item library assigned yet" : "No items match your filters",
        }}
      />
    </div>
  );
}
